/**
 * TanStack Query v5 queryOptions factories — convergence plan §6.2.
 *
 * Every cache key is site-/category-/epoch-/contract-aware so the
 * REST and fetch-SSE paths can never write different representations
 * of the same logical entity.
 *
 * Key shape:
 *   ['v1', site, category, epoch, schema_version, ...segments]
 *
 * The `v1Key()` helper in `./v1` is the canonical constructor.
 */
import { queryOptions, type QueryKey } from '@tanstack/react-query';

import {
  fetchAlerts,
  fetchAssignedSites,
  fetchCapabilities,
  fetchDoors,
  fetchFleets,
  fetchHealth,
  fetchLifts,
  fetchReady,
  fetchRobots,
  fetchSites,
  fetchTasks,
} from './rmf-client';
import {
  fetchBuildingMap,
  fetchDoorsRegistry,
  fetchDoorHealth,
  fetchDoorWriteGates,
  fetchLiftsRegistry,
  fetchLiftWriteGate,
} from './v1-client';
import { buildingMapSnapshotKey } from './scope';
import {
  applyRolePreviewToCapabilities,
  getPreviewRole,
  rememberRawCapabilities,
} from '../auth/role-preview';
import { v1Key } from './v1';
import type { SiteId } from './v1';

const DEFAULT_STALE_MS = 30_000;

/**
 * A fixed poll, no poll, or a poll that reads the last successful
 * result — the third form has exactly one caller
 * (`liftWriteGateQueryOptions`) and exists so a query can back itself
 * off once it has learned there is nothing on the other end.
 *
 * Typed structurally rather than against TanStack's `Query`: the only
 * thing any caller here needs off it is the settled data, and the
 * narrower parameter keeps `build()` from having to be generic over
 * the library's four query type parameters.
 */
type RefetchIntervalOption =
  | number
  | false
  | ((query: { state: { data?: unknown } }) => number | false);

export interface QueryOptions<T> {
  queryKey: QueryKey;
  queryFn: () => Promise<T>;
  staleTime?: number;
  enabled?: boolean;
  refetchInterval?: RefetchIntervalOption;
  /** Left undefined by every factory but one, which means TanStack's
   *  default of three retries. Set it to `false` where a failure is
   *  information rather than an accident and retrying it just
   *  multiplies the request count by four. */
  retry?: boolean | number;
}

function build<T>(opts: QueryOptions<T>) {
  return queryOptions({
    queryKey: opts.queryKey,
    queryFn: opts.queryFn,
    staleTime: opts.staleTime ?? DEFAULT_STALE_MS,
    enabled: opts.enabled,
    refetchInterval: opts.refetchInterval,
    // OMIT the key entirely when unset, rather than passing `undefined`.
    // TanStack treats an explicit `retry: undefined` as a value that
    // overrides `QueryClient.defaultOptions` instead of deferring to it, so
    // spelling it unconditionally silently replaced main.tsx's `retry: 1`
    // with TanStack's own default of 3 -- for the eleven factories that
    // never pass `retry`, i.e. almost every query in the app. It surfaced
    // as one failing test and three times the retry traffic against a
    // failing api-server.
    ...(opts.retry === undefined ? {} : { retry: opts.retry }),
  });
}

/** `/health` — liveness probe shape, polled every 30 s. */
export const healthQueryOptions = () =>
  build({ queryKey: ['v1', 'health'], queryFn: fetchHealth, refetchInterval: 30_000 });

/** `/ready` — readiness with component list (plan §3.7). */
export const readinessQueryOptions = () =>
  build({ queryKey: ['v1', 'readiness'], queryFn: fetchReady, refetchInterval: 30_000 });

/**
 * Authorized sites (plan §6.3). Sourced exclusively from
 * `nexsoss-api-server`; assignment removal invalidates this and
 * triggers a reconcile in SiteContext.
 */
export const sitesQueryOptions = () =>
  build({
    queryKey: ['v1', 'sites'],
    queryFn: () => fetchAssignedSites(),
    staleTime: 60_000,
  });

/**
 * Effective capability matrix for the (caller, site) pair (plan
 * §Safety and authorization).
 *
 * Accepts `SiteId | null` so the hook can be used directly with the
 * result of `useActiveSite().site`; when the caller has no
 * assignment, the query is disabled (per plan §6.3: "assignment
 * removal aborts streams, clears site caches, and reconciles
 * selection").
 */
export const capabilitiesQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? (['v1', site, 'capabilities', 'current', '1.0'] as const)
      : (['v1', '__none__', 'capabilities', 'current', '1.0'] as const),
    // The server answers for the REAL token. During a "View as role"
    // preview the answer is recomputed for the previewed role (and
    // switched off) before it reaches the cache; the raw answer is
    // kept so entering or leaving a preview can recompute the cached
    // copy in place (`RolePreviewProvider`). See `auth/role-preview.ts`.
    queryFn: async () => {
      const raw = await fetchCapabilities(site as SiteId);
      rememberRawCapabilities(site as SiteId, raw);
      return applyRolePreviewToCapabilities(raw, getPreviewRole());
    },
    enabled: site !== null,
    staleTime: 60_000,
  });

// ----------------------------------------------------------------------------
// Site-scoped category reads. Each key is site-/category-/epoch-aware
// so the REST and fetch-SSE paths can never write different
// representations of the same logical entity (plan §6.2).
//
// Each factory accepts `SiteId | null`; when null the query is
// disabled (plan §6.3) and consumers see a stable "no data" state.
// ----------------------------------------------------------------------------

export const fleetsQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? v1Key({ site, category: 'fleets', epoch: 'current', schema_version: '1.0' })
      : (['v1', '__none__', 'fleets', 'current', '1.0'] as const),
    queryFn: () => fetchFleets(site as SiteId),
    enabled: site !== null,
  });

export const robotsQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? v1Key({ site, category: 'robots', epoch: 'current', schema_version: '1.0' })
      : (['v1', '__none__', 'robots', 'current', '1.0'] as const),
    queryFn: () => fetchRobots(site as SiteId),
    enabled: site !== null,
  });

export const tasksQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? v1Key({ site, category: 'tasks', epoch: 'current', schema_version: '1.0' })
      : (['v1', '__none__', 'tasks', 'current', '1.0'] as const),
    queryFn: () => fetchTasks(site as SiteId),
    enabled: site !== null,
  });

export const alertsQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? (['v1', site, 'alerts', 'current', '1.0'] as const)
      : (['v1', '__none__', 'alerts', 'current', '1.0'] as const),
    queryFn: () => fetchAlerts(site as SiteId),
    enabled: site !== null,
  });

export const doorsQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? v1Key({ site, category: 'doors', epoch: 'current', schema_version: '1.0' })
      : (['v1', '__none__', 'doors', 'current', '1.0'] as const),
    queryFn: () => fetchDoors(site as SiteId),
    enabled: site !== null,
  });

export const liftsQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? v1Key({ site, category: 'lifts', epoch: 'current', schema_version: '1.0' })
      : (['v1', '__none__', 'lifts', 'current', '1.0'] as const),
    queryFn: () => fetchLifts(site as SiteId),
    enabled: site !== null,
  });

// ----------------------------------------------------------------------------
// Device REGISTRIES (the building's inventory, not its live state).
//
// Keyed like `capabilities` and the write gate rather than with
// `v1Key()`, and that is a correctness requirement, not a style choice:
// `v1Key({category: 'doors'})` is the key `useLiveData`'s SSE flush
// writes the live door list into, so a registry sharing it would be
// overwritten by the first live event to arrive -- taking the
// registry-only rows with it, which is the entire failure this pair of
// queries exists to fix.
//
// Static on the timescale a console is open: a new registry appears
// when someone deploys a new building map, so these are read on the
// same 5-minute footing as `building_map` rather than polled.
// ----------------------------------------------------------------------------

/**
 * A 200 whose body is not a list is not an inventory, and it must not
 * be cached as one. A reverse proxy answering an unknown API path with
 * an SPA index page, or an api-server predating this route, both do
 * exactly that; without this guard the merge downstream throws inside a
 * render and takes the whole Devices screen down, which is a worse
 * outcome than the empty list this feature already tolerates.
 */
function registryRowsOnly<T>(rows: T[]): T[] {
  return Array.isArray(rows) ? rows : [];
}

export const doorsRegistryQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? (['v1', site, 'doors_registry', 'current', '1.0'] as const)
      : (['v1', '__none__', 'doors_registry', 'current', '1.0'] as const),
    queryFn: async () => registryRowsOnly(await fetchDoorsRegistry(site as SiteId)),
    enabled: site !== null,
    staleTime: 5 * 60_000,
  });

export const liftsRegistryQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? (['v1', site, 'lifts_registry', 'current', '1.0'] as const)
      : (['v1', '__none__', 'lifts_registry', 'current', '1.0'] as const),
    queryFn: async () => registryRowsOnly(await fetchLiftsRegistry(site as SiteId)),
    enabled: site !== null,
    staleTime: 5 * 60_000,
  });

/**
 * The site's lift write gate. Keyed like `capabilities` rather than
 * with `v1Key()`: it is a REST-only api-server read, not one of the
 * live Zenoh categories, so it has no epoch to be aware of.
 *
 * Short `staleTime` and an active poll, unlike the 60 s capability
 * matrix. Arm state is the one thing on this screen that a person can
 * change from somewhere else entirely -- the edge box's own dashboard,
 * or another supervisor's console -- and it also drops to false on its
 * own whenever the bridge restarts (invariant I1: never persisted). A
 * console showing a stale "Armed" is the failure mode this whole
 * change exists to end, so it is refetched at roughly the rate an
 * operator would expect a light to go out.
 */
export const WRITE_GATE_POLL_MS = 15_000;

/**
 * The poll used once the api-server has answered 404 — five minutes,
 * not fifteen seconds.
 *
 * A 404 says this deployment has no read path for arm state, and that
 * does not change between two polls; it changes when someone deploys a
 * new api-server. Backing off rather than stopping outright is the
 * deliberate middle: a console left open across that deployment picks
 * the gate up on its own within five minutes, and a 404 that turns out
 * to have been a misrouted proxy hop does not strand the panel on
 * "not exposed" until someone thinks to reload.
 */
export const WRITE_GATE_ABSENT_POLL_MS = 300_000;

export const liftWriteGateQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? (['v1', site, 'lift_write_gate', 'current', '1.0'] as const)
      : (['v1', '__none__', 'lift_write_gate', 'current', '1.0'] as const),
    queryFn: () => fetchLiftWriteGate(site as SiteId),
    enabled: site !== null,
    staleTime: 10_000,
    // `null` is `fetchLiftWriteGate`'s answer for "this deployment
    // does not serve a write gate" -- a settled, successful read of an
    // absence, so the query is not in an error state and there is
    // nothing here to retry. See the constant above for why this backs
    // off instead of stopping.
    refetchInterval: (query) =>
      query.state.data === null ? WRITE_GATE_ABSENT_POLL_MS : WRITE_GATE_POLL_MS,
    // No retries, and this is a safety property rather than a
    // politeness one. Retrying taught the console nothing it did not
    // already know -- an api-server without the read path answers 404
    // four times as readily as once -- while a fifteen second poll
    // turned three silent retries into four requests every fifteen
    // seconds for as long as anyone left the Lifts tab open. What the
    // operator needs from a gate that cannot be read is the sentence
    // saying so, which the first response already supplies.
    retry: false,
  });

/**
 * Every door adapter's write gate at this site — ONE query, and every
 * door surface reads this same one.
 *
 * The panel above the doors table and the copy of it inside
 * `DoorDetailDrawer` are the same component over this key, so the two
 * cannot end up saying different things about the same adapter. Two
 * readings of one arm flag in one screen is the exact shape of the bug
 * the lift gate exists to end.
 *
 * Same polling rules as the lift gate above, for the same reasons: arm
 * state changes from places this console cannot see (the adapter's own
 * `POST /arm` break-glass, another supervisor's console) and drops to
 * false on its own whenever the adapter restarts, since it is never
 * persisted (invariant I1). A `null` — the api-server answering 404 —
 * backs the poll off to five minutes rather than stopping, so a
 * console left open across the deployment that adds the read path
 * picks the gate up on its own.
 */
export const doorWriteGatesQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? (['v1', site, 'door_write_gates', 'current', '1.0'] as const)
      : (['v1', '__none__', 'door_write_gates', 'current', '1.0'] as const),
    queryFn: () => fetchDoorWriteGates(site as SiteId),
    enabled: site !== null,
    staleTime: 10_000,
    refetchInterval: (query) =>
      query.state.data === null ? WRITE_GATE_ABSENT_POLL_MS : WRITE_GATE_POLL_MS,
    // No retries, and it is the same safety property the lift gate
    // documents: an api-server without the read path answers 404 as
    // readily the fourth time as the first, and what the operator
    // needs from a gate that cannot be read is the sentence saying so,
    // which the first response already supplies.
    retry: false,
  });

export const doorHealthQueryOptions = (site: SiteId | null) =>
  build({
    queryKey: site
      ? (['v1', site, 'door_health', 'current', '1.0'] as const)
      : (['v1', '__none__', 'door_health', 'current', '1.0'] as const),
    queryFn: () => fetchDoorHealth(site as SiteId),
    enabled: site !== null,
    staleTime: 10_000,
    // The same cadence as the write gate, and for the same reason: the
    // adapter republishes on a 5 s keepalive, so anything slower would
    // show a controller's death later than the adapter noticed it,
    // while anything faster buys nothing.
    refetchInterval: (query) =>
      query.state.data === null ? WRITE_GATE_ABSENT_POLL_MS : WRITE_GATE_POLL_MS,
    // No retries, for the gate's reason: an api-server without the read
    // path answers 404 as readily the fourth time as the first.
    retry: false,
  });

/** How long a level's raw snapshot is served from the cache. A new map
 *  arrives under a NEW revision, and the SSE `building_map_revision`
 *  notification invalidates every level (`useLiveData`), so this is
 *  effectively static. */
export const BUILDING_MAP_SNAPSHOT_STALE_MS = 10 * 60_000;

/**
 * One level's RAW `BuildingMapSnapshot`, under `buildingMapSnapshotKey`
 * (`scope.ts` says why the raw and the assembled shapes have separate
 * keys). The one definition both readers share: `useFloorplans` observes
 * it, and `useBuildingMap`'s assembled query takes its raw part through
 * `queryClient.fetchQuery` on it, so a floor either view has fetched is
 * not fetched again by the other.
 *
 * It replaces `buildingMapQueryOptions`, which nothing read: its `v1` key
 * sat outside the `[building_map, site]` prefix the revision notification
 * invalidates, so any reader would have held a superseded map.
 */
export const buildingMapSnapshotQueryOptions = (site: SiteId | null, level: string) =>
  build({
    queryKey: buildingMapSnapshotKey(site ?? '', level),
    queryFn: () => fetchBuildingMap(site as SiteId, level),
    enabled: Boolean(site),
    staleTime: BUILDING_MAP_SNAPSHOT_STALE_MS,
  });

// Re-export for screens that import from query-options directly.
export { fetchSites };